import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AsyncAdminService } from 'src/app/services/async-admin.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';
import * as alertifyjs from 'alertifyjs';

/**
 * Switch del perfil bancario (separación de contratos, 1-nov-2026).
 * Pantalla aparte, a la que se llega desde Pagos Asíncronos (contrato activo) y desde Parámetros del Sistema y
 * Gestión de Métodos de Pago (?pestana=cuentas abre «Cuentas por contrato»). Se usa una vez, en el corte;
 * existe para cambiar el contrato sin desplegar.
 */
@Component({
  selector: 'app-perfil-bancario',
  templateUrl: './perfil-bancario.component.html',
  styleUrls: ['./perfil-bancario.component.css']
})
export class PerfilBancarioComponent implements OnInit {

  perfilBancario: any = null;          // { activo, desde, historia[] }
  verificacionPerfil: any = null;      // { perfil, listo, puntos[] }
  readonly perfilObjetivo = 'CT';
  perfilLoading = false;
  perfilError: string | null = null;

  // Pestaña «Cuentas por contrato»: ahora la dibuja <app-cuentas-contrato>. Esto queda para la confirmación del switch (texto del corte)
  pestana: 'switch' | 'cuentas' = 'switch';
  cuentasPerfil: any = null;           // { perfil, grupos[], resumen, corte }
  cuentasLoading = false;
  cuentasError: string | null = null;
  vistaCuentas: 'hoy' | 'despues' = 'hoy';
  resultadoCambio: { tipo: 'exito' | 'alerta', texto: string } | null = null;
  /** Sube cuando cambia el contrato: la vista nueva de cuentas se vuelve a leer. */
  recargaCuentas = 0;

  constructor(private asyncAdminService: AsyncAdminService, public _auth: AuthService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('pestana') === 'cuentas') this.pestana = 'cuentas';
    this.loadPerfilBancario();
  }


  /** Estado del switch y, si está en MT, la verificación del perfil objetivo. No falla la carga del panel. */
  async loadPerfilBancario(): Promise<void> {
    if (!this._auth.hasPermission('config.perfil_bancario.read')) return;
    this.perfilLoading = true;
    this.perfilError = null;
    try {
      const estado = await this.asyncAdminService.getPerfilBancario();
      this.perfilBancario = estado?.data || null;
      this.verificacionPerfil = null;
      if (this.perfilBancario?.activo !== this.perfilObjetivo) {
        const v = await this.asyncAdminService.getVerificacionPerfil(this.perfilObjetivo);
        this.verificacionPerfil = v?.data || null;
      }
    } catch (error: any) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cargando perfil bancario:', error);
      this.perfilError = error?.error?.message || error?.error?.msg || 'No se pudo consultar el perfil bancario';
    } finally {
      this.perfilLoading = false;
    }
    await this.loadCuentasPerfil();
  }

  /** Cuentas por contrato. Un fallo aquí no tumba el resto de la pantalla. */
  async loadCuentasPerfil(): Promise<void> {
    if (!this._auth.hasPermission('config.perfil_bancario.read')) return;
    this.cuentasLoading = true;
    this.cuentasError = null;
    try {
      const r = await this.asyncAdminService.getCuentasPerfilBancario(this.perfilObjetivo);
      this.cuentasPerfil = r?.data || null;
    } catch (error: any) {
      console.error('❌ [PERFIL-BANCARIO] Error cargando cuentas por contrato:', error);
      this.cuentasPerfil = null;
      this.cuentasError = error?.error?.message || error?.error?.msg || 'No se pudieron consultar las cuentas por contrato';
    } finally {
      this.cuentasLoading = false;
    }
  }

  /** Cómo se dice cada estado de una fila. */
  textoEstado(estado: string): string {
    const textos: any = {
      lista: 'Lista',
      falta: 'Falta la cuenta de CT',
      sin_uso: 'Oculta: no necesita par',
      fuera: 'Fuera del corte'
    };
    return textos[estado] || estado;
  }

  /** Color de la insignia: verde si está lista, ámbar si falta algo, neutro en los demás. Nunca rojo. */
  claseEstado(estado: string): string {
    if (estado === 'lista') return 'kb-badge-exito';
    if (estado === 'falta') return 'kb-badge-alerta';
    return 'kb-badge-neutro';
  }

  /** Pago móvil y transferencia son los únicos usos que cambian de visibilidad en el corte. */
  private usoDelCorte(uso: string): boolean {
    return uso === 'pago_movil' || uso === 'transferencia';
  }

  /**
   * Una cuenta tal como se vería después del encendido. Con la regla del corte: la del perfil toma la visibilidad
   * que tenía la de MT; la de MT queda oculta pero activa (sigue recibiendo pagos tardíos).
   */
  private cuentaSimulada(cuenta: any, esPar: boolean, otra: any): any {
    if (!cuenta) return cuenta;
    if (esPar) return { ...cuenta, status: true, showClient: !!otra.showClient, showChatbot: !!otra.showChatbot };
    return { ...cuenta, showClient: false, showChatbot: false };
  }

  /** Filas de un grupo en la vista elegida (hoy o después del encendido). Solo pago móvil y transferencia con par cambian. */
  filasVista(grupo: any): any[] {
    const filas: any[] = grupo?.filas || [];
    if (this.vistaCuentas !== 'despues' || !this.usoDelCorte(grupo?.uso)) return filas;
    return filas.map((f) => (f.cuentaMT && f.par)
      ? { ...f, cuentaMT: this.cuentaSimulada(f.cuentaMT, false, f.par), par: this.cuentaSimulada(f.par, true, f.cuentaMT) }
      : f);
  }

  /** Dónde se ve una cuenta, en palabras. */
  dondeSeVe(c: any): string {
    const donde = [c?.showClient ? 'la app' : '', c?.showChatbot ? 'el chatbot' : ''].filter(Boolean);
    return donde.length ? donde.join(' y ') : 'no se ve en la app ni en el chatbot';
  }

  /** Verificación del pago de la cuenta, en palabras. */
  textoVerificacion(c: any): string {
    const t = String(c?.typeConfirm || '').toUpperCase();
    if (t === 'AUTO') return 'Automática';
    if (t === 'MANUAL') return 'Manual';
    return c?.typeConfirm || '—';
  }

  /** Cuántas cuentas de pago móvil y transferencia con par se mostrarán y se ocultarán en el corte. */
  resumenCorte(): { pagoMovil: number, transferencia: number, total: number } | null {
    if (!this.cuentasPerfil?.grupos) return null;
    const contar = (uso: string) => (this.cuentasPerfil.grupos.find((g: any) => g.uso === uso)?.filas || [])
      .filter((f: any) => f.cuentaMT && f.par).length;
    const pagoMovil = contar('pago_movil');
    const transferencia = contar('transferencia');
    return { pagoMovil, transferencia, total: pagoMovil + transferencia };
  }

  /** Texto que se agrega a la confirmación del cambio a CT (HTML sin datos de cuentas ni claves). */
  textoCorteConfirmacion(): string {
    if (this.cuentasPerfil?.corte) {
      return 'Ya hay un corte de cuentas aplicado: no se repite. Solo se cambia el contrato, y al volver a MT se deshace ese corte.';
    }
    const r = this.resumenCorte();
    if (!r) return '';
    if (!r.total) return 'No hay cuentas de pago móvil ni de transferencia con su par de CT: las cuentas no cambian.';
    const n = (cant: number, uno: string, varios: string) => `${cant} ${cant === 1 ? uno : varios}`;
    return `Se mostrarán <strong>${n(r.total, 'cuenta de CT', 'cuentas de CT')}</strong> de pago móvil y transferencia `
      + `(${r.pagoMovil} de pago móvil y ${r.transferencia} de transferencia) y se ocultarán sus ${r.total} pares de MT, `
      + 'que siguen activas para recibir pagos tardíos. Se guarda cómo estaban para poder volver.';
  }

  /** Lo que ocurrió con las cuentas al cambiar el perfil, dicho para un gerente. */
  resultadoDelCambio(respuesta: any, perfil: string): { tipo: 'exito' | 'alerta', texto: string } | null {
    const r = respuesta?.data && !respuesta.corte && !respuesta.vuelta && !respuesta.aviso ? respuesta.data : respuesta;
    const aviso = r?.aviso;
    const corte = r?.corte;
    const vuelta = r?.vuelta;
    if (aviso) {
      return { tipo: 'alerta', texto: `El perfil volvió a ${perfil}, pero las cuentas no se restauraron solas: ${typeof aviso === 'string' ? aviso : 'hay que restaurarlas aparte'}` };
    }
    if (corte) {
      if (corte.yaExistia) return { tipo: 'exito', texto: 'Ya había un corte de cuentas aplicado, así que no se repitió. Solo cambió el contrato.' };
      if (corte.aplicado) return { tipo: 'exito', texto: `Corte de cuentas aplicado: ${corte.cuentas} cuentas cambiadas. Se guardó cómo estaban para poder volver.` };
      return { tipo: 'exito', texto: 'No había cuentas por cambiar en el corte.' };
    }
    if (vuelta) return { tipo: 'exito', texto: `Se restauraron ${vuelta.restauradas} cuentas a como estaban antes del corte.` };
    return null;
  }

  /** Pasar a otro perfil exige el permiso y la verificación en verde; volver a MT solo el permiso. */
  puedeCambiarPerfil(perfil: string): boolean {
    if (this.perfilLoading || !this._auth.hasPermission('config.perfil_bancario.cambiar')) return false;
    if (!this.perfilBancario || this.perfilBancario.activo === perfil) return false;
    if (perfil === 'MT') return true;
    return this.verificacionPerfil?.perfil === perfil && this.verificacionPerfil?.listo === true;
  }

  /** Nombre legible de cada punto de la verificación (nunca se muestran valores de claves). */
  nombrePuntoPerfil(nombre: string): string {
    const nombres: any = {
      'payments-bdv': 'Verificación BDV',
      'verificacion-mercantil': 'Verificación Mercantil',
      'boton-bdv': 'Botón de Pagos BDV',
      'boton-mercantil': 'Botón de Pagos Mercantil',
      'banco-plaza': 'Débito Inmediato Banco Plaza',
      'receptores': 'Tabla de receptores (pago móvil y transferencia)',
      'empresa': 'Datos de la empresa en la pasarela',
      'nucleo:boton-bdv-tdd': 'Cuenta Botón BDV débito',
      'nucleo:boton-bdv-tdc': 'Cuenta Botón BDV crédito',
      'nucleo:boton-mercantil-tdd': 'Cuenta Botón Mercantil débito',
      'nucleo:boton-mercantil-tdc': 'Cuenta Botón Mercantil crédito',
      'nucleo:boton-mercantil-tdc-5434': 'Cuenta Botón Mercantil crédito (BIN 5434)',
      'nucleo:debito-inmediato': 'Cuenta Débito Inmediato',
      'nucleo:debito-inmediato-atascado': 'Cuenta Débito Inmediato (recuperación)',
      'nucleo:notificacion-bdv': 'Cuenta de notificación BDV',
      'nucleo:empresa': 'RIF y razón social en el núcleo (iguales a la pasarela)',
      'nucleo:cuentas-visibles': 'Cuentas de MT que ve el cliente, con su par',
      'pasarelas-pares': 'Revisión de la otra producción (VM o Cloud Run)',
      'mercantil-flujo': 'Teléfonos y cuentas de CT en la lista de Mercantil'
    };
    if (nombres[nombre]) return nombres[nombre];
    if (nombre.startsWith('pasarela ')) return `Otra pasarela: ${nombre.slice(9)}`;
    return nombre;
  }

  /**
   * Qué falta de un punto. Las cuentas de MT visibles sin par se listan una por una, con su teléfono o número
   * completo (vista administrativa); los demás puntos, por su nombre.
   */
  faltanDePunto(p: any): string[] {
    const sinPar: any[] = p?.detalle?.sinPar || [];
    if (p?.nombre !== 'nucleo:cuentas-visibles' || !sinPar.length) return [this.nombrePuntoPerfil(p.nombre)];
    return sinPar.map((c) => {
      const metodo = c.metodo === 'PAGO_MOVIL' ? 'Pago móvil' : c.metodo === 'TRANSFERENCIA' ? 'Transferencia' : (c.metodo || 'Cuenta');
      const donde = [c.app ? 'la app' : '', c.chatbot ? 'el chatbot' : ''].filter(Boolean).join(' y ');
      return `${metodo} ${c.telefono || c.numero || c.cuentaMT}${donde ? ` (visible en ${donde})` : ''}`;
    });
  }

  /**
   * La verificación agrupada en bloques que se entienden sin conocer el sistema:
   * qué se revisa, cuántos puntos están listos y, si falta algo, qué falta y cómo se resuelve.
   */
  gruposVerificacion(): any[] {
    const puntos: any[] = this.verificacionPerfil?.puntos || [];
    const perfil = this.verificacionPerfil?.perfil || this.perfilObjetivo;
    const grupos = [
      {
        titulo: `Claves del contrato de ${perfil} en cada servicio`,
        descripcion: 'Verificación BDV y Mercantil, los dos botones de pago y el Débito Inmediato tienen cargadas las claves que entregó cada banco, en los dos servidores de producción.',
        solucion: 'Cargar las variables del perfil en el servicio que falta y volver a verificar.',
        claves: true,
        es: (n: string) => ['payments-bdv', 'verificacion-mercantil', 'boton-bdv', 'boton-mercantil', 'banco-plaza', 'pasarelas-pares'].includes(n) || n.startsWith('pasarela ')
      },
      {
        titulo: 'Cuentas y teléfonos que reciben pagos',
        descripcion: `La pasarela sabe qué cuentas y teléfonos de pago móvil y transferencia son de ${perfil}, para verificar cada pago con el contrato correcto; los del Mercantil, además, tienen que estar en su lista para que se verifiquen allá.`,
        solucion: 'Cargar la tabla de receptores con el guion 03 de la pasarela, que también agrega los de Mercantil a su lista.',
        es: (n: string) => n === 'receptores' || n === 'mercantil-flujo'
      },
      {
        titulo: `RIF y razón social de ${perfil}`,
        descripcion: `Los comprobantes de ${perfil} imprimen su propio RIF y razón social; deben estar cargados y ser iguales en el núcleo y en la pasarela.`,
        solucion: 'Correr el guion 00 con el RIF y la razón social.',
        es: (n: string) => n === 'empresa' || n === 'nucleo:empresa'
      },
      {
        titulo: 'Cuentas que ve el cliente',
        descripcion: `El corte muestra las cuentas de ${perfil} y oculta sus pares de MT en pago móvil y transferencia. Una cuenta de MT que el cliente ve en la app o en el chatbot y no tiene su par de ${perfil} seguiría visible después del corte.`,
        solucion: `Crear su par con el guion 01, u ocultarla en la administración de cuentas antes del corte.`,
        es: (n: string) => n === 'nucleo:cuentas-visibles'
      },
      {
        titulo: `Cuentas de ${perfil} en el sistema`,
        descripcion: `Cada cuenta de MT de los botones, el Débito Inmediato y la notificación BDV tiene su cuenta equivalente de ${perfil}, donde se registrarán los pagos.`,
        solucion: 'Crear las cuentas que faltan (guion 01 del núcleo).',
        es: (n: string) => n.startsWith('nucleo:')
      }
    ];
    const usados = new Set<string>();
    const resultado: any[] = grupos.map((g) => {
      const suyos = puntos.filter((p) => !usados.has(p.nombre) && g.es(p.nombre));
      suyos.forEach((p) => usados.add(p.nombre));
      return { ...g, total: suyos.length, listos: suyos.filter((p) => p.listo).length, faltan: suyos.filter((p) => !p.listo).flatMap((p) => this.faltanDePunto(p)) };
    });
    const otros = puntos.filter((p) => !usados.has(p.nombre));
    if (otros.length) {
      resultado.push({ titulo: 'Otros', descripcion: 'Puntos adicionales de la verificación.', solucion: '', total: otros.length,
        listos: otros.filter((p) => p.listo).length, faltan: otros.filter((p) => !p.listo).map((p) => this.nombrePuntoPerfil(p.nombre)) });
    }
    return resultado.filter((g) => g.total > 0);
  }

  /**
   * Qué variable de entorno falta, en qué servicio y en qué servidor (VM o Cloud Run).
   * Los servicios solo dicen «está» o «falta» por campo; el nombre se arma con el prefijo de cada uno
   * (<PREFIJO>_<PERFIL>_<CAMPO>). Nunca se muestran valores.
   */
  variablesQueFaltan(): { servidor: string, servicio: string, variables: string[], error?: string }[] {
    const prefijos: any = {
      'payments-bdv': 'BDV', 'verificacion-mercantil': 'MERCANTIL', 'boton-bdv': 'BDV_BP',
      'boton-mercantil': 'MERCANTIL_BP', 'banco-plaza': 'BANCO_PLAZA'
    };
    const perfil = this.verificacionPerfil?.perfil || this.perfilObjetivo;
    const salida: any[] = [];
    const revisar = (p: any, servidorPorDefecto: string) => {
      if (!prefijos[p?.nombre] || p.listo) return;
      const servidor = p.detalle?.servidor || servidorPorDefecto;
      const servicio = this.nombrePuntoPerfil(p.nombre);
      if (p.detalle?.error) {
        salida.push({ servidor, servicio, variables: [], error: 'el servicio no respondió a la verificación' });
        return;
      }
      const campos = p.detalle?.campos || {};
      const variables = Object.keys(campos).filter((c) => campos[c] === 'falta').map((c) => `${prefijos[p.nombre]}_${perfil}_${c}`);
      salida.push({ servidor, servicio, variables });
    };
    for (const p of this.verificacionPerfil?.puntos || []) {
      if (p.nombre === 'pasarelas-pares') {
        if (!p.listo) salida.push({ servidor: p.detalle?.servidor || 'servidor actual', servicio: 'Pasarela', variables: ['PASARELAS_PARES'],
          error: 'no tiene configurada la otra pasarela, así que la otra producción no se está revisando' });
        continue;
      }
      if (String(p.nombre).startsWith('pasarela ')) {
        const otra = p.nombre.slice(9);
        if (p.detalle?.error) salida.push({ servidor: otra, servicio: 'Pasarela', variables: [], error: 'no respondió a la verificación' });
        for (const q of p.detalle?.puntos || []) revisar(q, otra);
      } else {
        revisar(p, 'servidor actual');
      }
    }
    return salida;
  }

  async cambiarPerfilBancario(perfil: string): Promise<void> {
    if (!this.puedeCambiarPerfil(perfil)) return;
    const de = this.perfilBancario?.activo;
    const result = await Swal.fire({
      title: `¿Cambiar el perfil bancario de ${de} a ${perfil}?`,
      html: perfil === 'MT'
        ? 'Las transacciones nuevas de los botones y del Débito Inmediato volverán a usar los contratos de <strong>MT</strong>.'
        : `Las transacciones nuevas de los botones y del Débito Inmediato usarán los contratos de <strong>${perfil}</strong>. Las que ya están en curso terminan con su perfil.`
          + (this.textoCorteConfirmacion() ? `<br><br>${this.textoCorteConfirmacion()}` : ''),
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo (queda en el historial)',
      inputPlaceholder: 'Ej.: Corte del 1-nov',
      inputValidator: (v: string) => (!v || !v.trim()) ? 'El motivo es obligatorio' : null,
      showCancelButton: true,
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Sí, pasar a ${perfil}`,
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;

    this.perfilLoading = true;
    this.resultadoCambio = null;
    try {
      const respuesta = await this.asyncAdminService.cambiarPerfilBancario(perfil, String(result.value).trim());
      alertifyjs.success(`Perfil bancario cambiado a ${perfil}`);
      this.resultadoCambio = this.resultadoDelCambio(respuesta, perfil);
      if (this.resultadoCambio?.tipo === 'alerta') alertifyjs.warning(this.resultadoCambio.texto);
    } catch (error: any) {
      console.error('❌ [ASYNC-ADMIN-DASHBOARD] Error cambiando perfil bancario:', error);
      if (error?.status === 409 && error?.error?.verificacion) {
        this.verificacionPerfil = error.error.verificacion;
        alertifyjs.error(`No se cambió: el perfil ${perfil} no está completo`);
      } else {
        alertifyjs.error(error?.error?.message || error?.error?.msg || 'No se pudo cambiar el perfil bancario');
      }
    } finally {
      this.perfilLoading = false;
    }
    await this.loadPerfilBancario();
    this.recargaCuentas++;
  }

}
